import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme: 'auto';
      size: 'flexible';
      language: string;
      action: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
let pendingScript: HTMLScriptElement | null = null;
const scriptSelector = 'script[data-careerlaunch-turnstile]';

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise && pendingScript?.isConnected) return scriptPromise;
  // If an extension or route transition removed the pending script, its old
  // promise can never settle. Start a replacement request instead.
  scriptPromise = null;
  pendingScript = null;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(scriptSelector);
    // A script can finish loading without defining the provider global (for
    // example after a browser extension intervenes). Its load event will not
    // fire again, so discard it and request a fresh script.
    if (existing?.dataset.careerlaunchTurnstileState === 'loaded') existing.remove();
    const script =
      document.querySelector<HTMLScriptElement>(scriptSelector) ?? document.createElement('script');
    pendingScript = script;
    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
    const onLoad = () => {
      cleanup();
      script.dataset.careerlaunchTurnstileState = 'loaded';
      if (window.turnstile) resolve();
      else reject(new Error('Turnstile unavailable'));
    };
    const onError = () => {
      cleanup();
      script.dataset.careerlaunchTurnstileState = 'failed';
      reject(new Error('Turnstile failed to load'));
    };
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    if (!script.isConnected) {
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.careerlaunchTurnstile = 'true';
      document.head.appendChild(script);
    }
  }).catch((error) => {
    // A failed script element never fires again. Removing it lets the explicit
    // retry render a fresh challenge instead of waiting on a stale element.
    document.querySelector<HTMLScriptElement>(scriptSelector)?.remove();
    scriptPromise = null;
    pendingScript = null;
    throw error;
  });

  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  onToken,
}: {
  siteKey: string;
  onToken: (token: string | null) => void;
}) {
  const { i18n, t } = useTranslation('auth');
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const labelId = useId();

  useEffect(() => {
    let cancelled = false;
    onToken(null);

    void loadTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'auto',
          size: 'flexible',
          language: i18n.resolvedLanguage?.startsWith('ar') ? 'ar' : 'en',
          action: 'register',
          callback: (token) => {
            setState('ready');
            onToken(token);
          },
          'expired-callback': () => onToken(null),
          'error-callback': () => {
            setState('error');
            onToken(null);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = null;
      }
    };
  }, [attempt, i18n.resolvedLanguage, onToken, siteKey]);

  const retry = () => {
    onToken(null);
    setState('loading');
    setAttempt((current) => current + 1);
  };

  return (
    <div className="turnstile" aria-labelledby={labelId}>
      <span id={labelId} className="sr-only">
        Security check
      </span>
      <div ref={containerRef} className="turnstile__widget" />
      {state === 'loading' && <p className="field-hint">{t('register.challengeLoading')}</p>}
      {state === 'error' && (
        <div className="space-y-2">
          <p className="field-error" role="alert">
            {t('register.challengeError')}
          </p>
          <button type="button" className="button button--outline button--sm" onClick={retry}>
            {t('register.challengeRetry')}
          </button>
        </div>
      )}
    </div>
  );
}
